// RetentionHintSeen — 「登録すると部屋が長く残せます」初回 hint (P2) を
// **グローバルに 1 回だけ** 出すための seen フラグ永続化 (infrastructure 層)。
//
// 背景 (retention-communication-2026-07-14.md P2、consult 2026-07-14):
//   - 匿名オーナーのみ、初回だけ、消える/閉じられるバッジで「登録すると部屋が
//     長く残せます →」と促す。既存の New! バッジ (timelapseShareSeen /
//     pastCanvasSeen) の seen フラグ流儀を **そっくり複製** して別 key で管理する。
//   - seen は **グローバル 1 回** (`oekaki:retention-hint-seen`)。部屋ごとに毎回
//     出して nag しない。一度 dismiss / 表示したら以後どの部屋でも出さない。
//
// 設計 (`timelapseShareSeen.ts` と同流儀):
//   - boolean を文字列 `'1'` で保存 (= 在れば seen 済)。不在 / storage 不可は
//     「未 seen」(= まだ出す) 扱い。例外を throw しない。
//   - StorageLike 抽象 + sentinel probe。test で storage mock 注入可能。
//
// Tests: retentionHintSeen.test.ts (storage-injection style)。

/** localStorage キー: retention 初回 hint を出し終えたか (= グローバル 1 回)。 */
export const RETENTION_HINT_SEEN_KEY = 'oekaki:retention-hint-seen';

/** Minimal Storage shape this module uses (timelapseShareSeen.ts と同流儀)。 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface RetentionHintSeenStore {
  /** 既に hint を出し終えたか。不在 / storage 不可 → false (= まだ出す)。 */
  hasSeen(): boolean;
  /** hint を出した (= dismiss / 表示した) ことを記録。storage 不可なら no-op。 */
  markSeen(): void;
  /** key を削除。テスト / リセット用。storage 不可なら no-op。 */
  clear(): void;
}

const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:retention-hint-seen__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export interface CreateRetentionHintSeenStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * fall through to no-op if unavailable. Pass `null` to **force** the no-op
   * path (= hasSeen always false, markSeen silently drops).
   */
  storage?: StorageLike | null;
}

/**
 * `RetentionHintSeenStore` のインスタンスを構築。storage 不可でも例外を出さず、
 * 「未 seen」扱い (= 出すが永続化されないだけ) で動作を継続する。
 */
export const createRetentionHintSeenStore = (
  opts: CreateRetentionHintSeenStoreOptions = {},
): RetentionHintSeenStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  return {
    hasSeen() {
      if (storage === null) return false;
      try {
        return storage.getItem(RETENTION_HINT_SEEN_KEY) !== null;
      } catch {
        return false;
      }
    },
    markSeen() {
      if (storage === null) return;
      try {
        storage.setItem(RETENTION_HINT_SEEN_KEY, '1');
      } catch {
        // QuotaExceeded / SecurityError 等 — 出し直しになるだけで害はない。
      }
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(RETENTION_HINT_SEEN_KEY);
      } catch {
        // ignore
      }
    },
  };
};
